// Phase 25 — shared segment-filter UI.
//
// The combine-mode + axis checkboxes + status ternary, extracted from the
// Phase-22 contact-list modal so the bulk-mailer reuses the IDENTICAL filter
// surface. Presentational: the parent owns the filter state object (see
// defaultSegmentFilter / makeSegmentEvaluator in lib/collectiveSegments.mjs)
// and passes it down with a single onChange that merges a patch.
//
// Phase 25 adds the docs-in-order axis (inverse of docs-missing) alongside the
// three original axes. When neither docs axis is ticked, behaviour is
// identical to Phase 22.

import React from "react"; // 212 §22 — react/react-in-jsx-scope's own remedy (189 F.1)
import { KNOWN_STATUSES } from "../lib/collectiveSegments.mjs";
import { useTranslation } from "../i18n";
import { RAD, FS } from "../lib/tokens";
import { ListNotLoaded } from "./SharedUI"; // 212 §22 — the document tracking, when it could not load
import { useListsNotLoaded } from "../lib/listLoad";

type CollectiveSegmentFilterProps = {
  filter: any;
  onChange?: (patch: any) => void;
  hasDocTypes?: boolean;
  hasRounds?: boolean;
  /** 212 §22 — the host's key for its document-tracking read: when it could not load, its mark stands where the documents' axes would. */
  docsList?: string;
  compact?: boolean;
};

export default function CollectiveSegmentFilter({
  filter,
  onChange, // (patch) => void — shallow-merged into filter by the parent
  hasDocTypes,
  hasRounds,
  docsList,
  compact = false,
}: CollectiveSegmentFilterProps) {
  const { t } = useTranslation();
  const set = (patch: any) => onChange && onChange(patch);
  const docsNotLoaded = useListsNotLoaded(docsList ? [docsList] : []).length > 0;

  const lbl = { fontSize: FS.s11, color: "var(--tp)" };
  const muted = { fontSize: FS.s10, color: "var(--tm)" };
  const box = {
    border: "1px solid var(--br)",
    borderRadius: RAD.r6,
    padding: 10,
    marginBottom: compact ? 6 : 10,
    background: "var(--bi)",
  };
  const statusLabel = (s: string) =>
    t("cc.retainer.status." + s.toLowerCase(), { fallback: s });

  const setStatus = (s: string) =>
    set({ statusSel: { ...filter.statusSel, [s]: !filter.statusSel[s] } });

  return (
    <div style={box}>
      <div style={{ ...lbl, fontWeight: 600, marginBottom: 8 }}>
        {t("contactList.filters", { fallback: "Filters" })}
      </div>

      {/* Combine mode */}
      <div style={{ display: "flex", gap: 14, marginBottom: 10, flexWrap: "wrap" }}>
        <label style={{ ...lbl, display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
          <input
            type="radio"
            name="seg-combine"
            checked={filter.combine === "AND"}
            onChange={() => set({ combine: "AND" })}
          />
          {t("contactList.match_all", { fallback: "Match ALL selected filters (AND)" })}
        </label>
        <label style={{ ...lbl, display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
          <input
            type="radio"
            name="seg-combine"
            checked={filter.combine === "OR"}
            onChange={() => set({ combine: "OR" })}
          />
          {t("contactList.match_any", { fallback: "Match ANY selected filter (OR)" })}
        </label>
      </div>

      {/* Axis (i) — Documents missing */}
      {hasDocTypes && (
        <label style={{ ...lbl, display: "flex", alignItems: "center", gap: 6, marginBottom: 6, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={!!filter.useDocs}
            onChange={() => set({ useDocs: !filter.useDocs })}
          />
          {t("contactList.axis_docs", { fallback: "Missing at least one requested document" })}
        </label>
      )}

      {/* Axis (i-inverse) — Documents in order (Phase 25) */}
      {hasDocTypes && (
        <label style={{ ...lbl, display: "flex", alignItems: "center", gap: 6, marginBottom: 6, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={!!filter.useDocsInOrder}
            onChange={() => set({ useDocsInOrder: !filter.useDocsInOrder })}
          />
          {t("contactList.axis_docs_in_order", { fallback: "All requested documents received (docs in order)" })}
        </label>
      )}

      {/* 212 §22 — the document tracking not loaded: its mark in the axes' place (nothing while it is loaded) */}
      {!hasDocTypes && docsList && docsNotLoaded && (
        <div style={{ marginBottom: 6 }}>
          <ListNotLoaded lists={[docsList]} variant="inline" />
        </div>
      )}

      {/* Axis (ii) — Payment due */}
      {hasRounds && (
        <label style={{ ...lbl, display: "flex", alignItems: "center", gap: 6, marginBottom: 6, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={!!filter.usePay}
            onChange={() => set({ usePay: !filter.usePay })}
          />
          {t("contactList.axis_pay", { fallback: "Has an outstanding balance (payment due)" })}
        </label>
      )}

      {/* Axis (iii) — Status */}
      <label style={{ ...lbl, display: "flex", alignItems: "center", gap: 6, marginBottom: 4, cursor: "pointer" }}>
        <input
          type="checkbox"
          checked={!!filter.useStatus}
          onChange={() => set({ useStatus: !filter.useStatus })}
        />
        {t("contactList.axis_status", { fallback: "Status is one of:" })}
      </label>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", paddingLeft: 22, marginBottom: 4 }}>
        {KNOWN_STATUSES.map((s) => (
          <label
            key={s}
            style={{ ...muted, display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer" }}
          >
            <input
              type="checkbox"
              checked={!!filter.statusSel[s]}
              onChange={() => setStatus(s)}
            />
            {statusLabel(s)}
          </label>
        ))}
      </div>
      <div style={{ ...muted, paddingLeft: 22 }}>
        {/* 141 correction [RULED 2026-08-27] — Prospect joined the hard
            exclusion (see `matches` in collectiveSegments.mjs for why), so
            the note names three. Same mechanics as the last time this
            sentence changed: a seeded string is frozen (the seed only
            inserts), so `contactList.terminated_archived_note` retires in
            place beside `contactList.terminated_note` — the SECOND retirement
            of this one sentence. */}
        {t("contactList.prospect_terminated_archived_note", {
          fallback:
            "Prospect, terminated and archived members are excluded unless ticked above.",
        })}
      </div>
    </div>
  );
}
